import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Trophy, Target, Users, Calendar, ChevronRight } from 'lucide-react';

const FLAG_URL = (code) => `https://flagcdn.com/w80/${code?.toLowerCase()}.png`;

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/dashboard').then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-center text-white/50 py-10">Cargando...</div>;

  const stats = [
    { icon: Trophy, label: 'Mis Puntos', value: data?.user_points || 0, color: '#F4E39E' },
    { icon: Target, label: 'Predicciones', value: data?.my_predictions || 0, color: '#00FF87' },
    { icon: Calendar, label: 'Partidos', value: data?.total_matches || 0, color: '#60A5FA' },
    { icon: Users, label: 'Jugadores', value: data?.total_users || 0, color: '#F472B6' },
  ];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="dashboard-page">
      <div className="text-center lg:text-left">
        <h2 className="text-2xl lg:text-3xl font-extrabold text-white" style={{ fontFamily: 'Montserrat' }}>
          Bienvenido, <span className="text-[#F4E39E]">{user?.user_id}</span>
        </h2>
        <p className="text-white/50 text-sm mt-1">Panel de Quiniela Mundial 2026</p>
      </div>

      {/* Stats Grid - 2 cols mobile, 4 cols desktop */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4">
        {stats.map((s, i) => (
          <div key={i} className="glass-card p-4 lg:p-6 flex flex-col items-center gap-2" data-testid={`stat-${s.label.toLowerCase().replace(/\s/g, '-')}`}>
            <s.icon size={24} className="lg:w-7 lg:h-7" style={{ color: s.color }} />
            <span className="text-2xl lg:text-3xl font-bold text-white" style={{ fontFamily: 'Montserrat' }}>{s.value}</span>
            <span className="text-xs text-white/50">{s.label}</span>
          </div>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="flex gap-3 lg:gap-4">
        <button onClick={() => navigate('/predicciones')} className="flex-1 gold-btn py-3 lg:py-4 rounded-xl text-sm lg:text-base font-bold flex items-center justify-center gap-2" data-testid="predict-button">
          <Target size={16} /> Predecir
        </button>
        <button onClick={() => navigate('/ranking')} className="flex-1 gold-btn-outline py-3 lg:py-4 rounded-xl text-sm lg:text-base font-semibold flex items-center justify-center gap-2" data-testid="ranking-button">
          <BarChart3 size={16} /> Ranking
        </button>
      </div>

      {/* Upcoming Matches */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg lg:text-xl font-bold text-white" style={{ fontFamily: 'Montserrat' }}>Proximos Partidos</h3>
          <button onClick={() => navigate('/partidos')} className="text-[#F4E39E] text-sm flex items-center gap-1 hover:underline">
            Ver todos <ChevronRight size={14} />
          </button>
        </div>

        {(!data?.upcoming_matches || data.upcoming_matches.length === 0) ? (
          <div className="glass-card p-6 text-center text-white/40">No hay partidos proximos</div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-3 lg:gap-4">
            {data.upcoming_matches.map((match, i) => (
              <div key={i} className="glass-card p-4" data-testid={`upcoming-match-${i}`}>
                <div className="text-xs text-[#F4E39E] font-semibold mb-2">{match.stage}</div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <img src={FLAG_URL(match.home_team?.flag_code)} alt="" className="flag-img" onError={(e) => e.target.style.display='none'} />
                    <span className="text-white font-bold text-sm">{match.home_team?.code}</span>
                  </div>
                  <span className="text-[#F4E39E] font-black text-lg" style={{ fontFamily: 'Montserrat' }}>VS</span>
                  <div className="flex items-center gap-2">
                    <span className="text-white font-bold text-sm">{match.away_team?.code}</span>
                    <img src={FLAG_URL(match.away_team?.flag_code)} alt="" className="flag-img" onError={(e) => e.target.style.display='none'} />
                  </div>
                </div>
                <div className="text-white/40 text-xs mt-2 text-center">
                  {match.date} - {match.time}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function BarChart3(props) {
  return <Trophy {...props} />;
}
